// CodeMirror editor themes matching the "Statistical Report" paper
// palette (the same `--bg-0/1/2`, `--fg-0/1/2` etc. CSS variables that
// drive the toolbar / sidebar / output panel). Two bundles, one per
// page theme — each is `EditorView.theme(...)` for the editor chrome
// plus a `HighlightStyle` for SAS syntax tags.
//
// Ink text on paper; syntax colors come from the semantic paper set
// (cyan / green / amber / red / magenta); selection is translucent
// duck yellow. Duck yellow never colors text.

import { EditorView } from '@codemirror/view';
import type { Extension } from '@codemirror/state';
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { tags as t } from '@lezer/highlight';

interface Palette {
  bg0: string;
  bg1: string;
  bg2: string;
  fg0: string;
  fg1: string;
  fg2: string;
  border: string;
  cyan: string;
  green: string;
  amber: string;
  red: string;
  magenta: string;
  /// Translucent duck-yellow selection; matches ::selection and the
  /// package components' --selection token.
  selection: string;
  /// Background for in-document search matches (amber tints so they
  /// stay distinct from the yellow selection).
  searchMatch: string;
  searchMatchSel: string;
  /// Active-line wash — a whisper of ink, not a surface swap.
  activeLine: string;
}

const night: Palette = {
  bg0: '#171511',
  bg1: '#201d16',
  bg2: '#29251c',
  fg0: '#ece7da',
  fg1: '#c9c2b1',
  fg2: '#a49e8e',
  border: '#35322a',
  cyan: '#8fb8c4',
  green: '#7cc8a0',
  amber: '#d9bd66',
  red: '#e08573',
  magenta: '#b99cbd',
  selection: 'rgba(255, 217, 57, 0.22)',
  searchMatch: 'rgba(217, 189, 102, 0.25)',
  searchMatchSel: 'rgba(217, 189, 102, 0.50)',
  activeLine: 'rgba(236, 231, 218, 0.05)',
};

const paper: Palette = {
  bg0: '#f7f5ef',
  bg1: '#efece1',
  bg2: '#e9e5d8',
  fg0: '#1a1917',
  fg1: '#3d3a33',
  fg2: '#5f5c54',
  border: '#d9d5c9',
  cyan: '#275e6e',
  green: '#1e6b46',
  amber: '#8a6d1f',
  red: '#9e2b25',
  magenta: '#7c5f80',
  selection: 'rgba(255, 217, 57, 0.4)',
  searchMatch: 'rgba(138, 109, 31, 0.18)',
  searchMatchSel: 'rgba(138, 109, 31, 0.40)',
  activeLine: 'rgba(26, 25, 23, 0.045)',
};

function makeChrome(p: Palette, isDark: boolean): Extension {
  return EditorView.theme(
    {
      '&': {
        color: p.fg0,
        backgroundColor: p.bg0,
      },
      '.cm-content': {
        caretColor: p.fg0,
      },
      '.cm-cursor, .cm-dropCursor': {
        borderLeftColor: p.fg0,
      },
      // Selection: focused + unfocused + native ::selection all share
      // the same translucent duck yellow so the visible state is
      // consistent regardless of focus.
      '&.cm-focused .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection': {
        backgroundColor: p.selection,
      },
      '.cm-activeLine': {
        backgroundColor: p.activeLine,
      },
      '.cm-gutters': {
        backgroundColor: p.bg1,
        color: p.fg2,
        border: 'none',
        borderRight: `1px solid ${p.border}`,
      },
      '.cm-activeLineGutter': {
        backgroundColor: p.bg2,
        color: p.fg0,
      },
      '.cm-foldPlaceholder': {
        backgroundColor: p.bg2,
        color: p.fg1,
        borderColor: p.border,
      },
      '.cm-tooltip': {
        backgroundColor: p.bg1,
        border: `1px solid ${p.fg0}`,
        borderRadius: '0',
        color: p.fg0,
      },
      '.cm-tooltip .cm-tooltip-arrow:before': {
        borderTopColor: p.fg0,
        borderBottomColor: p.fg0,
      },
      '.cm-tooltip .cm-tooltip-arrow:after': {
        borderTopColor: p.bg1,
        borderBottomColor: p.bg1,
      },
      '.cm-tooltip-autocomplete > ul > li[aria-selected]': {
        backgroundColor: p.selection,
        color: p.fg0,
      },
      '.cm-panels': {
        backgroundColor: p.bg1,
        color: p.fg0,
      },
      '.cm-panels.cm-panels-bottom': {
        borderTop: `2px solid ${p.fg0}`,
      },
      '.cm-panels.cm-panels-top': {
        borderBottom: `2px solid ${p.fg0}`,
      },
      '.cm-searchMatch': {
        backgroundColor: p.searchMatch,
        outline: `1px solid ${p.amber}`,
      },
      '.cm-searchMatch.cm-searchMatch-selected': {
        backgroundColor: p.searchMatchSel,
      },
      '.cm-diagnostic-error': { borderLeft: `3px solid ${p.red}` },
      '.cm-diagnostic-warning': { borderLeft: `3px solid ${p.amber}` },
      '.cm-diagnostic-info': { borderLeft: `3px solid ${p.cyan}` },
    },
    { dark: isDark },
  );
}

function makeHighlight(p: Palette): HighlightStyle {
  return HighlightStyle.define([
    { tag: [t.keyword, t.controlKeyword, t.modifier, t.definitionKeyword], color: p.cyan, fontWeight: 'bold' },
    { tag: t.string, color: p.green },
    { tag: [t.number, t.bool, t.null], color: p.amber },
    { tag: [t.comment, t.lineComment, t.blockComment, t.docComment], color: p.fg2, fontStyle: 'italic' },
    { tag: [t.meta, t.processingInstruction], color: p.magenta },
    { tag: t.invalid, color: p.red, textDecoration: 'underline' },
    { tag: [t.operator, t.compareOperator, t.logicOperator, t.arithmeticOperator], color: p.fg1 },
    { tag: t.punctuation, color: p.fg1 },
    { tag: t.variableName, color: p.fg0 },
    { tag: t.propertyName, color: p.fg0 },
    { tag: t.typeName, color: p.magenta },
  ]);
}

/// Editor theme bundle for the dark ("night reading") page theme —
/// chrome plus SAS-syntax highlight styles. Drop into the editor's
/// extensions array (e.g. through a Compartment so it can be swapped
/// at runtime).
export const kolilangDark: Extension = [
  makeChrome(night, true),
  syntaxHighlighting(makeHighlight(night), { fallback: true }),
];

export const kolilangLight: Extension = [
  makeChrome(paper, false),
  syntaxHighlighting(makeHighlight(paper), { fallback: true }),
];
